import React, { useEffect, useState, useRef, useCallback } from 'react'
import ProfileCard from './ProfileCard'
import { Announce } from '../interfaces/announce'
import { User } from '../interfaces/user'
import { fetchUsers } from '../../services/fetchAllUser'
import calculateAge from '../fonctions/calculateAge'
import { useSearchFilters } from './SearchFiltersContext'
import { calculateDistance } from '../fonctions/calculateDistance'
import citiesData from '../../src/data/cities.json'
import { API_BASE_URL, X_API_KEY } from '../../services/api'
import { useTranslation } from 'react-i18next'
import { NoAnnouncesFound } from './NoAnnouncesFound'
import { ActiveFilters } from './ActiveFilters'

const ITEMS_PER_PAGE = 12

const distanceRanges: { [key: string]: number[] } = {
  '< 10km': [0, 10],
  '< 20km': [0, 20],
  '< 50km': [0, 50],
  '< 75km': [0, 75],
  '< 100km': [0, 100],
}

const getCityCoordinates = (cityName: string) => {
  for (const province of citiesData) {
    const city = province.cities.find(c => c.city_fr === cityName)
    if (city) {
      return { latitude: city.latitude, longitude: city.longitude }
    }
  }
  return { latitude: 0, longitude: 0 }
}

const defaultImageUrl = (gender: string | undefined) => {
  const baseUrl = window.location.origin

  switch (gender) {
    case 'man':
      return `${baseUrl}/images/silouhette-male.jpg`
    case 'woman':
      return `${baseUrl}/images/silouhette-female.jpg`
    case 'transgender':
      return `${baseUrl}/images/silouhette-transgender.jpg`
    case 'couple':
      return `${baseUrl}/images/silouhette-couple.jpg`
    default:
      return `${baseUrl}/images/silouhette.jpg`
  }
}

const AnnouncesPrivate: React.FC = () => {
  const { t } = useTranslation()
  const [announces, setAnnounces] = useState<Announce[]>([])
  const [filteredAnnounces, setFilteredAnnounces] = useState<Announce[]>([])
  const [displayedAnnounces, setDisplayedAnnounces] = useState<Announce[]>([])
  const [users, setUsers] = useState<User[]>([])
  const { filters, setFilters } = useSearchFilters()
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [statusToShow, setStatusToShow] = useState([1])
  const [initialLoading, setInitialLoading] = useState(true)

  const observer = useRef<IntersectionObserver>()
  const lastAnnounceElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          setPage(prevPage => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [loading, hasMore],
  )

  useEffect(() => {
    const fetchData = async () => {
      setInitialLoading(true)
      try {
        const usersData = await fetchUsers()
        setUsers(usersData)

        const response = await fetch(`${API_BASE_URL}/announces`, {
          headers: {
            'x-api-key': X_API_KEY || '',
          },
        })
        if (!response.ok) throw new Error('Failed to fetch announces')
        const data = await response.json()

        await new Promise(resolve => setTimeout(resolve, 500))

        const dataMap = new Map()
        data.forEach((announce: any) => {
          dataMap.set(announce.id, {
            boostedTime: announce.boostedTime,
            createdAt: announce.createdAt,
          })
        })

        let announceIds = data
          .sort((a: any, b: any) => {
            const dateA =
              a.boostedTime && a.boostedTime !== 'null'
                ? new Date(a.boostedTime).getTime()
                : new Date(a.createdAt).getTime()

            const dateB =
              b.boostedTime && b.boostedTime !== 'null'
                ? new Date(b.boostedTime).getTime()
                : new Date(b.createdAt).getTime()

            return dateB - dateA
          })
          .map((announce: { id: string }) => announce.id)

        const announceDetails: any[] | ((prevState: Announce[]) => Announce[]) =
          []
        const detailsMap = new Map()

        for (const id of announceIds) {
          try {
            const response = await fetch(`${API_BASE_URL}/announces/${id}`, {
              headers: {
                'x-api-key': X_API_KEY || '',
              },
            })
            if (!response.ok) continue

            const announceData = await response.json()
            const user = usersData.find(
              user => user.id === announceData.announce.userId,
            )

            if (!announceData.announce.status.includes('private')) continue

            const filteredGallery = (announceData.gallery || []).filter(
              (img: any) =>
                img.status.toString().includes(statusToShow.toString()),
            )

            detailsMap.set(id, {
              ...announceData.announce,
              boostedTime: dataMap.get(id).boostedTime,
              createdAt: dataMap.get(id).createdAt,
              age: user ? calculateAge(user.birthday) : null,
              user: user || {
                id: 0,
                birthday: '',
                nickname: '',
                userProfile: { gender: '', orientation: '' },
                address: { city: '' },
              },
              galleryUrl:
                filteredGallery.find((img: any) => img.cover)?.fileUrl ||
                filteredGallery[0]?.fileUrl ||
                defaultImageUrl(user?.userProfile?.gender),
              gallery: filteredGallery,
            })
          } catch (error) {
            console.error(`Error fetching announce ${id}:`, error)
          }
        }

        announceIds.forEach((id: any) => {
          const details = detailsMap.get(id)
          if (details) {
            announceDetails.push(details)
          }
        })

        setAnnounces(announceDetails)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setInitialLoading(false)
      }
    }

    fetchData()
  }, [statusToShow])

  useEffect(() => {
    const filtered = announces.filter(announce => {
      const user = users.find(u => u.id === announce.user.id)
      if (!user || user.isBanned) return false

      if (!announce.status.includes('private')) return false

      const matchesJeCherche =
        filters.imLookingFor.length === 0 ||
        (filters.imLookingFor.length > 0 &&
          filters.imLookingFor.includes(user.userProfile.gender))

      const matchesOrientation =
        filters.orientation.length === 0 ||
        (filters.orientation.length > 0 &&
          filters.orientation.includes(user.userProfile.orientation))

      let matchesLocation = filters.iLiveIn.length === 0

      if (!matchesLocation && filters.iLiveIn.length > 0) {
        const filterCity = getCityCoordinates(filters.iLiveIn[0])
        const userCity = getCityCoordinates(user.address.city)

        if (filterCity && userCity) {
          if (filters.distance.length === 0) {
            matchesLocation = user.address.city === filters.iLiveIn[0]
          } else {
            const distance = calculateDistance(
              filterCity.latitude,
              filterCity.longitude,
              userCity.latitude,
              userCity.longitude,
            )

            const [, maxDistance] = distanceRanges[filters.distance[0]] || [
              0,
              Infinity,
            ]
            matchesLocation = distance <= maxDistance
          }
        }
      }

      return matchesJeCherche && matchesOrientation && matchesLocation
    })

    if (filters.iLiveIn.length > 0 && filters.distance.length > 0) {
      const referenceCity = getCityCoordinates(filters.iLiveIn[0])
      if (referenceCity) {
        filtered.sort((a, b) => {
          const cityA = getCityCoordinates(a.user.address.city)
          const cityB = getCityCoordinates(b.user.address.city)
          if (cityA && cityB) {
            const distanceA = calculateDistance(
              referenceCity.latitude,
              referenceCity.longitude,
              cityA.latitude,
              cityA.longitude,
            )
            const distanceB = calculateDistance(
              referenceCity.latitude,
              referenceCity.longitude,
              cityB.latitude,
              cityB.longitude,
            )
            return distanceA - distanceB
          }
          return 0
        })
      }
    }

    setFilteredAnnounces(filtered)
    setPage(1)
    setDisplayedAnnounces([])
  }, [filters, users, announces])

  useEffect(() => {
    setLoading(true)
    const startIndex = (page - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    const newAnnounces = filteredAnnounces.slice(startIndex, endIndex)

    setDisplayedAnnounces(prev => [
      ...prev,
      ...newAnnounces.filter(
        newAnnounce => !prev.some(announce => announce.id === newAnnounce.id),
      ),
    ])
    setHasMore(endIndex < filteredAnnounces.length)
    setLoading(false)
  }, [page, filteredAnnounces])

  const handleRemoveFilter = (type: string) => {
    const newFilters = { ...filters }

    switch (type) {
      case 'location':
        newFilters.iLiveIn = []
        newFilters.distance = []
        break
      case 'looking':
        newFilters.imLookingFor = []
        break
      case 'orientation':
        newFilters.orientation = []
        break
      case 'distance':
        newFilters.distance = []
        break
    }

    setFilters(newFilters)
    localStorage.setItem('searchFilters', JSON.stringify(newFilters))
  }

  useEffect(() => {
    const savedFilters = localStorage.getItem('searchFilters')
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters)
        const cleanedFilters = {
          imLookingFor:
            parsedFilters.imLookingFor?.filter((item: string) => item !== '') ||
            [],
          orientation:
            parsedFilters.orientation?.filter((item: string) => item !== '') ||
            [],
          iLiveIn:
            parsedFilters.iLiveIn?.filter((item: string) => item !== '') || [],
          distance:
            parsedFilters.distance?.filter((item: string) => item !== '') || [],
        }
        setFilters(cleanedFilters)
      } catch (error) {
        console.error('Error parsing saved filters:', error)
      }
    }
  }, [setFilters])

  return (
    <>
      <div>
        <ActiveFilters filters={filters} onRemoveFilter={handleRemoveFilter} />
      </div>
      {initialLoading ? (
        <div className='flex justify-center items-center min-h-[200px]'>
          <div className='animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-redpink'></div>
        </div>
      ) : (
        <div className='px-2 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-4 md:gap-2'>
          {announces.length === 0 ? (
            <div className='col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-4 xl:col-span-6'>
              <NoAnnouncesFound
                hasActiveFilters={Object.values(filters).some(
                  filterArray => filterArray.length > 0,
                )}
              />
            </div>
          ) : displayedAnnounces.length > 0 ? (
            displayedAnnounces.map((announce, index) => (
              <div
                key={announce.id}
                ref={
                  index === displayedAnnounces.length - 1
                    ? lastAnnounceElementRef
                    : null
                }
              >
                <ProfileCard
                  announce={announce}
                  user={announce.user}
                  statusToShow={statusToShow}
                  gallery={announce.gallery || []}
                />
              </div>
            ))
          ) : (
            <div className='col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-4 xl:col-span-6'>
              <NoAnnouncesFound
                hasActiveFilters={Object.values(filters).some(
                  filterArray => filterArray.length > 0,
                )}
              />
            </div>
          )}
          {loading && (
            <div className='col-span-2 text-center'>{t('page-loading')}</div>
          )}
        </div>
      )}
    </>
  )
}

export default AnnouncesPrivate
